import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useSessionCtx } from "../../context/SessionContext.jsx";
import { SelectInput, Button, TextInput } from "../../components/Elements.jsx";
import {
  generateStreamingLink,
  useStreamingVendors,
} from "../../api/adminHook.js";
import {
  toggleStreamToUser,
  getSessionById,
  setSessionDetails,
  setStreamingVendorLocal,
} from "../../hooks/adminStore.js";
import { refreshCmeSessionOnce } from "../../hooks/useCmeSessionSync.js";
import { checkStreamPlaying, playbackReasonText } from "../../hooks/useStreamPlayback.js";
import { streamingUrlOf } from "../../utils/streamingUrl.js";
import { deriveStreamHealth } from "../../utils/streamHealth.js";
import { checkLinkPlayable, linkCheckReasonText, LINK_CHECK_TIMEOUT_MS } from "../../utils/linkPlayable.js";
import { isWebPageVendor } from "../../utils/webPageStream.js";
import { normalizeVendor } from "../../meeting/vendors.js";
import { BROADCAST_BLOCKED, isZoomBroadcastUrl } from "../../zoom/frameMessages.js";
import { GoLiveCheckModal } from "./GoLiveCheckModal.jsx";
import { getGoLiveTimingOverrides } from "./goLiveTiming.js";
import useMediaQuery from "../../hooks/useMediaQuery.js";

// Go Live's "Stream health" check (Mux telemetry on cme-common's data.health - "No
// streaming health data" / "Stream isn't ready yet") before "Stream playing".
// true = skipped: Go Live relies on the "Stream playing" check alone. Skipped while the
// backend publishes no health for a live Mux stream. Only Go Live reads this - the Stream
// Health card on the dashboard shows either way. false = required again.
export const SKIP_STREAM_HEALTH_CHECK = true;

// Admin streaming controls for a Zoom session. Always shown - it does not wait
// for the admin to join the Zoom meeting. Reads 3 facts from session.data: hosting.vendor, streaming.vendor,
// streaming.steaming_url. Lets the admin publish a link and start/stop
// the stream, gated so viewers only ever see "live" once it's real.

// ---------- small helpers ----------

// Dropdown label vs. a plain vendor code rarely match exactly - fuzzy match, ignoring case,
// spaces, dashes and underscores (list "Web Page" = cme-common "webpage").
function vendorLabelMatches(label, value) {
  if (!label || !value) return false;
  const squash = (text) => String(text).trim().toLowerCase().replace(/[\s_-]+/g, "");
  const a = squash(label);
  const b = squash(value);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

// The "Web Page" streaming vendor (isWebPageVendor): its stream is a link the admin
// enters, sent to the publish API as `source`.

// How a cme-common streaming.vendor code reads in the steps when the vendor list has no
// entry for it (not loaded yet, or a vendor this meeting vendor doesn't list).
function vendorDisplayName(code) {
  if (!code) return "";
  return isWebPageVendor(code) ? "Web Page" : String(code);
}

// A full http(s) link: typed with its scheme ("http://" or "https://"), no spaces, and a
// real host - a domain with a dot, localhost, or an IP. new URL() alone would also accept
// "http:cdn.example" or "https://cdn".
function isHttpUrl(value) {
  const text = String(value ?? "");
  if (!/^https?:\/\/\S+$/i.test(text)) return false;
  try {
    const { protocol, hostname } = new URL(text);
    if (protocol !== "http:" && protocol !== "https:") return false;
    return hostname.includes(".") || hostname === "localhost" || hostname.startsWith("[");
  } catch {
    return false;
  }
}

// Exact match against a known vendor code (not fuzzy, unlike vendorLabelMatches).
function isVendorCode(value, code) {
  return (
    String(value || "")
      .trim()
      .toLowerCase() === code
  );
}

// Comparable vendor name: lowercase, no spaces/dashes/underscores. Known
// aliases (e.g. "rtk" -> "realtimekit") via normalizeVendor(); unknown names are
// kept as-is, since normalizeVendor() would turn every unknown into "zoom".
function canonicalVendorName(raw) {
  const stripped = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (!stripped) return "";
  const known = normalizeVendor(raw);
  return known !== "zoom" ? known : stripped;
}

// Hosting vendor === streaming vendor (zoom+zoom, realtimekit+realtimekit, ...):
// one service hosts and streams, so no streaming_url is needed. Both must be set.
function isSameVendorStream({ hostingVendor, streamingVendor }) {
  const host = canonicalVendorName(hostingVendor);
  return Boolean(host) && host === canonicalVendorName(streamingVendor);
}

// Is there any way to go live at all? Same-vendor stream: always. Anything
// else: only once a real streaming URL has been generated.
function hasAStreamPath({ hostingVendor, streamingVendor, streamingUrl }) {
  if (isSameVendorStream({ hostingVendor, streamingVendor })) return true;
  return Boolean(streamingUrl);
}

// Every fact the go-live gate needs, read from one session object. Used both
// at render time and again on a freshly force-fetched session (see
// startStream) so the two never drift apart.
function deriveGoLiveFacts(session) {
  const hostingVendor = session?.data?.hosting?.vendor || "";
  const streamingVendor = session?.data?.streaming?.vendor || "";
  // The same url the Stream Health card checks (shared helper), so both read one status.
  const streamingUrl = streamingUrlOf(session?.data?.streaming);
  const isSessionLive = session?.data?.detail?.status === 2; // 2 = "Live Now"
  // Same vendor name on both sides: skip the streaming_url requirement.
  const isSameVendor = isSameVendorStream({ hostingVendor, streamingVendor });
  // Zoom hosting a different streaming vendor: whether the stream is really on
  // comes from the Zoom SDK's custom-livestream toggle. zoom+zoom skips it
  // (meeting is the stream); other hosts have no such toggle. A Web Page stream is the
  // admin's own link, not fed by Zoom - no toggle for it either.
  const isHostingZoom = isVendorCode(hostingVendor, "zoom");
  const isWebPageStream = isWebPageVendor(streamingVendor);
  const needsZoomSdkToggle = isHostingZoom && !isSameVendor && !isWebPageStream;
  const streamPathReady = hasAStreamPath({
    hostingVendor,
    streamingVendor,
    streamingUrl,
  });
  return {
    hostingVendor,
    streamingVendor,
    streamingUrl,
    isSessionLive,
    isSameVendor,
    isHostingZoom,
    isWebPageStream,
    needsZoomSdkToggle,
    // Both steps "Meeting (...)" and "Vendor (...)" must be in cme-common before
    // anything can go live - without them there is nothing defined to stream.
    hasVendors: Boolean(hostingVendor) && Boolean(streamingVendor),
    canGoLive: isSessionLive && streamPathReady,
  };
}

// ---------- Zoom SDK live-streaming toggle ----------
// The iframe (ZoomClientFrame.jsx) owns the real Zoom meeting UI. We can only
// ASK it to turn on Zoom's "Live on Custom Live Streaming Service" control
// via postMessage - it reports back whether that actually worked.

// Turning Zoom's custom live streaming on: the frame first works through More -> Livestream
// (up to ~10s), then waits up to 32s for Zoom to connect the stream (Zoom's own limit is
// 30s) - wait longer than both together. Turning it off is immediate in Zoom, plus menus.
const ZOOM_START_CONFIRM_MS = 50000;
const ZOOM_STOP_CONFIRM_MS = 15000;

// Waits for the iframe's "zoom-livestream-status" report. No reply in time =
// treated as not-live (never assume success we can't confirm). acceptPassiveLive: the
// frame's 2s passive monitor seeing Zoom go live is confirmation too - no need to wait
// for the toggle's own report.
function waitForZoomLivestreamStatus(timeoutMs = 6000, { acceptPassiveLive = false } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    function handleMessage(e) {
      if (e.origin !== window.location.origin || e.data?.source !== "cme-zoom-frame") return;
      if (e.data?.kind === "zoom-livestream-status") {
        finish(Boolean(e.data.active));
      } else if (
        acceptPassiveLive &&
        e.data?.kind === "zoom-livestream-status-passive" &&
        e.data.active === true
      ) {
        finish(true);
      }
    }
    function finish(result) {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", handleMessage);
      clearTimeout(timer);
      resolve(result);
    }
    const timer = setTimeout(() => finish(false), timeoutMs);
    window.addEventListener("message", handleMessage);
  });
}

async function queryZoomLivestreamStatus(timeoutMs = 2000) {
  const zoomFrame = document.querySelector('iframe[title="Zoom meeting"]');
  if (!zoomFrame?.contentWindow) return false;
  const p = waitForZoomLivestreamStatus(timeoutMs);
  zoomFrame.contentWindow.postMessage(
    { kind: "query-zoom-livestream-status" },
    window.location.origin,
  );
  return p;
}

// Ask the SDK iframe to turn on live streaming, then wait for confirmation.
// Safe to call even if it's already on - it just confirms that.
// First queries if already live to avoid flickering More menu when manually started.
async function enableZoomSdkLivestreamAndConfirm() {
  const zoomFrame = document.querySelector('iframe[title="Zoom meeting"]');
  // No Zoom frame (admin not in the meeting): nothing can turn it on - answer at once
  // instead of waiting out the confirmation timeout.
  if (!zoomFrame?.contentWindow) return false;
  const alreadyLive = await queryZoomLivestreamStatus(1500);
  if (alreadyLive) return true;
  // Listen before asking, so the frame's report can never arrive first.
  const confirmed = waitForZoomLivestreamStatus(ZOOM_START_CONFIRM_MS, { acceptPassiveLive: true });
  zoomFrame.contentWindow.postMessage(
    { kind: "toggle-zoom-stream", streamState: true },
    window.location.origin,
  );
  return confirmed;
}

// Ask the SDK iframe to turn custom live streaming off, then wait for its report.
// Zoom keeps streaming to the RTMP target it was started with, so a new streaming
// vendor needs the old stream stopped and turned on again. No Zoom frame (admin not
// in the meeting) = nothing is streaming from here, nothing to stop.
async function disableZoomSdkLivestream() {
  const zoomFrame = document.querySelector('iframe[title="Zoom meeting"]');
  if (!zoomFrame?.contentWindow) return;
  const reported = waitForZoomLivestreamStatus(ZOOM_STOP_CONFIRM_MS);
  zoomFrame.contentWindow.postMessage(
    { kind: "toggle-zoom-stream", streamState: false },
    window.location.origin,
  );
  await reported;
}

// Never let a hanging/rejecting refresh decide the fate of the Go Live button: the
// rendered session is always an acceptable fallback for the gates below, and the
// rejection is swallowed here so it can't surface as an unhandled promise (which
// reads to the admin as "I clicked and nothing happened").
const REFRESH_TIMEOUT_MS = 8000;
function refreshWithFallback(sessionId, fallback) {
  const p = Promise.resolve(refreshCmeSessionOnce(sessionId)).catch(() => fallback);
  let timer;
  return Promise.race([
    p,
    new Promise((resolve) => {
      timer = setTimeout(() => resolve(fallback), REFRESH_TIMEOUT_MS);
    }),
  ]).finally(() => clearTimeout(timer));
}

// ---------- Go Live modal (GoLiveCheckModal) ----------
// Display pacing only - the checks themselves are the same as before.
const GO_LIVE_TIMING = {
  minRunMs: 400, // a check that answers at once still shows its "checking" line this long
  passHoldMs: 700, // the green tick stays up this long before the next check
  liveHoldMs: 1800, // "You're live" stays up this long, then the modal closes itself
  // Pause between two playing checks while the stream isn't playable yet (the old "Stream
  // not ready" modal's RECHECK_MS): an unreachable url answers at once, so this keeps the
  // loop from hammering the CDN.
  playbackRecheckMs: 3000,
};
const goLiveTiming = () => ({ ...GO_LIVE_TIMING, ...getGoLiveTimingOverrides() });

// Countdowns shown for the two checks that have a time limit of their own: the
// cme-common refresh (refreshWithFallback) and the Zoom toggle (enableZoomSdkLivestreamAndConfirm:
// a 1.5s "already on?" query, then Zoom's confirmation).
const FETCH_COUNTDOWN_MS = REFRESH_TIMEOUT_MS;
const ZOOM_COUNTDOWN_MS = 1500 + ZOOM_START_CONFIRM_MS;

// Zoom's broadcast page opened from the blocked pop-up notice closes this long after Zoom is
// live - as ZoomClientFrame does with the tab Zoom opens itself.
const BROADCAST_TAB_CLOSE_DELAY_MS = 2000;

// What the admin is told when Zoom's custom live streaming couldn't be confirmed.
const ZOOM_TURN_ON_YOURSELF_MSG =
  'We couldn\'t confirm the Zoom meeting is live streaming yet. Open the meeting\'s "More" menu and turn on "Live on Custom Live Streaming Service" yourself.';

// The sticky toast (setStickyToast) shown while Zoom must be streaming but isn't.
const ZOOM_STREAM_OFF_TOAST = "zoom-stream-off";
export const ZOOM_STREAM_OFF_MSG =
  'Zoom streaming is off. Please turn it on: in the Zoom meeting, open "More" and choose "Live on Custom Live Streaming Service".';

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// How many checks Go Live shows for these facts: fetch, meeting vendor, streaming vendor,
// session live; a URL stream adds link, health and playing, Zoom hosting it adds the toggle;
// a Web Page stream adds link and link playable only. One less while the health check is
// skipped (SKIP_STREAM_HEALTH_CHECK).
function goLiveCheckCount(facts) {
  if (facts.isSameVendor) return 4;
  if (facts.isWebPageStream) return 6; // link, then link playable - no Zoom, no health
  return (facts.needsZoomSdkToggle ? 8 : 7) - (SKIP_STREAM_HEALTH_CHECK ? 1 : 0);
}

// The Change Stream vendor dropdown, memoized on purpose. The controls re-render on every
// session update (cme-common health ticks arrive every second or two), and React
// re-applies a controlled <select>'s selection on each re-render (option.selected = true)
// - which makes the browser close an open dropdown list, so it snapped shut within a
// second. Memoized with stable props, it only re-renders when its own inputs change.
const VendorSelect = memo(function VendorSelect({ value, onChange, vendors, isLoading, isError }) {
  const hasSelection = vendors.some((v) => String(v.vendorid) === String(value));
  return (
    <SelectInput
      value={value}
      onChange={onChange}
      className={`!py-2 w-full text-xs sm:text-sm${!hasSelection && !isLoading ? " !border-red-500" : ""}`}
    >
      <option value="" disabled>
        {isLoading
          ? "Loading vendors…"
          : isError
            ? "Failed to load vendors"
            : vendors.length
              ? "Select Streaming Vendor"
              : "No other streaming vendor available"}
      </option>
      {vendors.map((v) => (
        <option key={v.vendorid} value={v.vendorid}>
          {v.vendor}
        </option>
      ))}
    </SelectInput>
  );
});

// Pipeline steps (Meeting -> Streaming -> Stream -> Live), a tick per step.
// `hidden` steps are tracked but never drawn.
function StepIndicator({ steps: allSteps }) {
  const steps = allSteps.filter((step) => !step.hidden);
  return (
    <div className="flex items-center">
      {steps.map((step, idx) => (
        <div key={step.id} className="flex items-center" title={step.label}>
          <div
            className={`flex items-center gap-1.5 ${step.isComplete ? "text-black" : "text-gray-400"}`}
          >
            <div
              className={`flex items-center justify-center shrink-0 w-3.5 h-3.5 rounded-full transition-colors duration-300 ${step.isComplete ? "bg-emerald-500" : "bg-gray-200"}`}
            >
              {step.isComplete && (
                <svg
                  className="w-2.5 h-2.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
            <span className="text-[11px] font-medium whitespace-nowrap">
              {step.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className={`w-2 sm:w-4 h-[1.5px] mx-1.5 rounded-full transition-colors duration-300 ${step.isComplete ? "bg-emerald-400" : "bg-gray-200"}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// Below md the top bar has no room for the steps, so they are portaled into this slot
// (rendered by MainPage above the Stream Health row). No slot on the page = not shown.
export const MOBILE_STEPS_SLOT_ID = "mobile-stream-steps";

export function StreamingVendorControls() {
  const { id: routeSessionId } = useParams();
  const ctx = useSessionCtx();
  const session = ctx?.session || {};
  const showMsg = ctx?.showMsg;
  const sessionId = session.sessionId ?? routeSessionId;

  // Tracks if custom stream is actually running in iframe. Stays false until the
  // Zoom iframe reports it (e.g. before the admin has joined the meeting).
  const [isZoomLive, setIsZoomLive] = useState(false);
  // Zoom's live state in the frame's FIRST passive report on this page (null = none yet).
  // The passive monitor only runs once the admin is in the meeting, so this is "what the
  // meeting looked like right after (re)joining" - see the resume effect below.
  const [zoomStateOnJoin, setZoomStateOnJoin] = useState(null);
  // Zoom's broadcast page when the browser's pop-up blocker stopped it (the iframe reports
  // it): without that page Zoom never goes live. The Go Live modal says so and offers to
  // open it from the admin's click, which pop-up blockers allow.
  const [blockedBroadcastUrl, setBlockedBroadcastUrl] = useState(null);
  const broadcastTabRef = useRef(null); // that page as opened from here

  // Listen for the Zoom iframe's live-stream status reports.
  useEffect(() => {
    function handleMessage(e) {
      if (e.origin === window.location.origin && e.data?.source === "cme-zoom-frame") {
        if (
          e.data?.kind === "zoom-livestream-status-passive" ||
          e.data?.kind === "zoom-livestream-status"
        ) {
          if (typeof e.data.active === "boolean") {
            setIsZoomLive(e.data.active);
            if (e.data.kind === "zoom-livestream-status-passive") {
              const active = e.data.active;
              setZoomStateOnJoin((first) => first ?? active);
            }
          }
        } else if (e.data?.kind === BROADCAST_BLOCKED && isZoomBroadcastUrl(e.data.url)) {
          setBlockedBroadcastUrl(e.data.url);
        }
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // The broadcast page opened from the blocked pop-up notice has done its job once Zoom is
  // live: close it, so the admin is back on the dashboard.
  useEffect(() => {
    const tab = broadcastTabRef.current;
    if (!isZoomLive || !tab) return undefined;
    const timer = setTimeout(() => {
      broadcastTabRef.current = null;
      try {
        if (!tab.closed) tab.close();
      } catch {
        // already gone
      }
    }, BROADCAST_TAB_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isZoomLive]);

  // < sm: Go Live floats full-width at the bottom (rendered only there, not just hidden).
  const isPhone = useMediaQuery("(max-width: 639.98px)", false);

  // Slot for the < md steps row. The page renders it only on small screens, in the
  // same commit as this media-query flip, so it exists when this effect runs.
  const isBelowMd = useMediaQuery("(max-width: 767.98px)", false);
  const [mobileStepsSlot, setMobileStepsSlot] = useState(null);
  useEffect(() => {
    setMobileStepsSlot(
      isBelowMd ? document.getElementById(MOBILE_STEPS_SLOT_ID) : null,
    );
  }, [isBelowMd]);

  // --- UI state ---
  const [showPublishModal, setShowPublishModal] = useState(false); // pick vendor + generate link
  // The Go Live modal's current check (see GoLiveCheckModal); null = closed.
  const [goLiveCheck, setGoLiveCheck] = useState(null);
  // The running Go Live: { cancelled }. Cancel flags it: no further check runs and it
  // never goes live.
  const goLiveRunRef = useRef(null);
  const [vendorId, setVendorId] = useState(""); // dropdown selection
  const [webPageUrl, setWebPageUrl] = useState(""); // the link sent as `source`, "Web Page" vendor only
  // Generate was pressed with the link box empty: say so under the box, not just in a toast.
  const [webPageUrlRequired, setWebPageUrlRequired] = useState(false);

  // One lock shared by Generate / Start / Stop, so only one of these ever
  // runs at a time - no two async flows can interleave or race each other.
  const [busy, setBusy] = useState(false);

  // --- the facts everything below is decided from ---
  // (Derived before the vendor-list query because its URL depends on the
  // hosting vendor.)
  const {
    hostingVendor,
    streamingVendor,
    streamingUrl,
    isSameVendor,
    isHostingZoom,
    isWebPageStream,
    needsZoomSdkToggle,
  } = deriveGoLiveFacts(session);
  // After a page reload the admin re-joins the Zoom meeting - and Zoom ends a custom live
  // stream when its host leaves, so a session that was live is left on a dead stream (and
  // the Zoom Stream step unticked). So, decided once at the frame's first report after
  // joining: Zoom isn't live, yet everything else says it should be - the session is live
  // (streamToUser), Zoom hosts another streaming vendor (not zoom+zoom, not Web Page) and
  // the stream link exists - turn Custom Live Streaming back on by itself. It never takes
  // the busy lock, so Stop Live still works meanwhile (the frame runs one toggle at a time,
  // newest wins).
  const zoomResumeDecidedRef = useRef(false);
  const streamIsLive = Boolean(session.data?.streamToUser);
  useEffect(() => {
    if (zoomStateOnJoin === null || zoomResumeDecidedRef.current) return;
    zoomResumeDecidedRef.current = true;
    if (zoomStateOnJoin !== false || !needsZoomSdkToggle || !streamingUrl || !streamIsLive) return;
    enableZoomSdkLivestreamAndConfirm().then((on) => {
      if (on) showMsg?.("Zoom Custom Live Streaming resumed after the page reload", "success");
      else showMsg?.(ZOOM_TURN_ON_YOURSELF_MSG, "error");
    });
  }, [zoomStateOnJoin, needsZoomSdkToggle, streamingUrl, streamIsLive, showMsg]);

  // Session live for viewers (streamToUser), with Zoom hosting another streaming vendor (the
  // Zoom Stream step) and the stream link in place: Zoom's Custom Live Streaming is what
  // feeds that link, so while the frame doesn't report it on (or the admin isn't in the
  // meeting yet) a toast says so - one that can't be closed and stays until Zoom is live or
  // the stream is stopped. Not live: nothing to warn about, Go Live turns Zoom on itself.
  // Leaving the page clears it.
  const setStickyToast = ctx?.setStickyToast;
  const zoomStreamOff = streamIsLive && needsZoomSdkToggle && Boolean(streamingUrl) && !isZoomLive;
  useEffect(() => {
    if (!setStickyToast) return undefined;
    setStickyToast(ZOOM_STREAM_OFF_TOAST, zoomStreamOff ? ZOOM_STREAM_OFF_MSG : null);
    return () => setStickyToast(ZOOM_STREAM_OFF_TOAST, null);
  }, [zoomStreamOff, setStickyToast]);

  // The stream button and the modal it opens: "Add Stream" until the session has a streaming
  // vendor (cme-common streaming.vendor), "Change Stream" once one is set.
  const streamActionLabel = streamingVendor ? "Change Stream" : "Add Stream";

  // Streaming vendors offered for THIS session's hosting vendor:
  // GET cme/vendor/list/{realtimekit|zoom|100ms}. normalizeVendor() maps the
  // raw hosting.vendor (any case, legacy Zoom spellings like "zoommsdk") to
  // the canonical key the API expects. With no hosting vendor configured yet
  // the key is null, so the query stays disabled instead of guessing "zoom".
  const hostingVendorKey = hostingVendor ? normalizeVendor(hostingVendor) : null;
  const {
    data: streamVendors = [],
    isLoading: isVendorsLoading,
    isError: isVendorsError,
    refetch: refetchVendors,
  } = useStreamingVendors(hostingVendorKey);
  // No streaming vendor yet = nothing can be streamed: the admin must pick one before
  // using the page, so Add Stream opens by itself and can't be closed. (This only renders
  // once SessionGate has the session loaded and live.) Needs the meeting vendor - the
  // vendor list is per meeting vendor. streamChosen bridges the moment between a successful
  // Generate and cme-common's streaming.vendor arriving, so the modal doesn't pop back.
  const [streamChosen, setStreamChosen] = useState(false);
  const mustChooseStream = Boolean(hostingVendor) && !streamingVendor && !streamChosen;
  // No meeting vendor: nothing can be picked here at all (the list is per meeting vendor),
  // so the page is locked behind a notice until admin sets one - it clears by itself
  // when cme-common's hosting.vendor arrives.
  const missingMeetingVendor = !hostingVendor;
  // The list entry for the vendor CME is streaming with right now (cme-common
  // streaming.vendor). Change Stream hides it: re-picking the live vendor would only
  // regenerate the link that is already running.
  const liveVendor = streamingVendor
    ? streamVendors.find((v) => vendorLabelMatches(v.vendor, streamingVendor))
    : null;
  const liveVendorId = liveVendor?.vendorid;
  // The "Streaming (...)" step's name, straight from cme-common's streaming.vendor - so it
  // reads the same after a refresh: the list's own label when it lists that vendor,
  // otherwise a readable form of the code ("webpage" -> "Web Page").
  const streamingVendorName = liveVendor?.vendor || vendorDisplayName(streamingVendor);
  // Web Page is always offered, even while it is the live vendor: submitting a new src
  // for it is a real change, unlike re-picking any other live vendor.
  const hideLiveVendor = liveVendorId != null && !isWebPageVendor(liveVendor?.vendor);
  // Memoized so the dropdown's props stay identical across session updates (see VendorSelect).
  const selectableVendors = useMemo(
    () =>
      hideLiveVendor
        ? streamVendors.filter((v) => String(v.vendorid) !== String(liveVendorId))
        : streamVendors,
    [streamVendors, liveVendorId, hideLiveVendor],
  );
  const onVendorChange = useCallback((e) => setVendorId(e.target.value), []);
  // Only an option that is actually offered can be generated.
  const selectedVendor = selectableVendors.find(
    (v) => String(v.vendorid) === String(vendorId),
  );
  // "Web Page" streams a src the admin types in - show the url box and send it along.
  const needsWebPageUrl = isWebPageVendor(selectedVendor?.vendor);
  const trimmedWebPageUrl = webPageUrl.trim();
  const webPageUrlError = !needsWebPageUrl
    ? ""
    : trimmedWebPageUrl
      ? isHttpUrl(trimmedWebPageUrl)
        ? ""
        : "Enter a full link starting with http:// or https://"
      : webPageUrlRequired
        ? "Enter the web page stream link"
        : "";

  const steps = [
    {
      id: "host",
      label: hostingVendor ? `Meeting (${hostingVendor})` : "Meeting",
      isComplete: !!hostingVendor,
    },
    {
      id: "vendor",
      label: streamingVendorName ? `Streaming (${streamingVendorName})` : "Streaming",
      isComplete: !!streamingVendor,
    },
    // Same vendor on both sides = no streaming link to generate, hide checkbox (user request)
    // Not drawn at all (user request). Display only: Go Live checks the streaming url
    // itself (hasAStreamPath / checkStreamPlaying), not this step.
    ...(!isSameVendor
      ? [{ id: "url", label: "Stream Link", hidden: true, isComplete: !!streamingUrl }]
      : []),
    // Zoom hosting another vendor: ticked only once the Zoom SDK reports custom
    // live streaming on. Other hosts: ticked once the link exists. Same vendor
    // (zoom+zoom, rtk+rtk, ...): hidden, nothing to turn on (user request). Web Page:
    // hidden too - its link is what the admin typed, Zoom streams nothing for it.
    ...(needsZoomSdkToggle
      ? [{ id: "zoom", label: "Zoom Stream", isComplete: isZoomLive }]
      : !isSameVendor && !isWebPageStream
        ? [{ id: "zoom", label: "Stream", isComplete: Boolean(streamingUrl) }]
        : []),
    { id: "live", label: "Live", isComplete: !!session.data?.streamToUser },
  ];

  // Toast once when the streaming-vendor list fails to load (e.g. a 404 from
  // /cme/vendor/list). This only stays a one-off because showMsg has a stable
  // identity (useCallback in useSessionState): showMsg sets state, so if its
  // identity changed every render this effect would re-run on each render and
  // loop forever ("Maximum update depth exceeded"). The effect re-fires only
  // when isVendorsError flips, i.e. once per failure.
  useEffect(() => {
    if (isVendorsError) showMsg?.("Failed to load streaming vendors", "error");
  }, [isVendorsError, showMsg]);

  // Disable "Generate" when the pick is already live, or is really just the
  // hosting vendor's own name (that case needs no link at all).

  // The modal always opens on "Select…" - the live vendor is hidden, and a pick left
  // over from last time may since have become the live one. Streaming a Web Page now:
  // its link box starts with that page's current link (cme-common), ready to edit.
  function openPublishModal() {
    setVendorId("");
    setWebPageUrl(isWebPageStream ? streamingUrl : "");
    setWebPageUrlRequired(false);
    setShowPublishModal(true);
  }

  function closePublishModal() {
    setVendorId("");
    setWebPageUrl("");
    setWebPageUrlRequired(false);
    setShowPublishModal(false);
  }

  async function handleGenerateStreamingLink() {
    if (!selectedVendor || busy) return;
    if (!sessionId) {
      showMsg?.("No session available to generate a streaming link", "error");
      return;
    }
    // "Web Page": the typed link is the stream - never send an empty or malformed one.
    if (needsWebPageUrl && !isHttpUrl(trimmedWebPageUrl)) {
      setWebPageUrlRequired(true);
      showMsg?.("Enter the web page stream link (http:// or https://)", "error");
      return;
    }
    // Picked the hosting vendor itself (zoom+zoom, rtk+rtk, ...): the meeting is the stream.
    const picksHostingVendor = isSameVendorStream({
      hostingVendor,
      streamingVendor: selectedVendor.vendor,
    });
    setBusy(true);
    try {
      // 1. ask the backend to generate the link ("Web Page": publish the src the admin entered)
      await generateStreamingLink(hostingVendor, {
        session_id: sessionId,
        metting_id: session.data?.hosting?.meeting_id, // backend's field name, not a typo
        vendor_id: selectedVendor.vendorid,
        ...(needsWebPageUrl ? { source: trimmedWebPageUrl } : {}),
      });
      showMsg?.("Streaming link generated!");
      setShowPublishModal(false);
      setStreamChosen(true);

      // 2. the vendor changed: stop the previous Zoom custom live stream (it still points
      //    at the old vendor). Only after the generate succeeded, so a failed generate
      //    leaves the running stream alone.
      if (isHostingZoom) await disableZoomSdkLivestream();

      // 3. Zoom hosting another vendor (Mux, ...): turn "Live on Custom Live Streaming
      //    Service" back on for the new vendor right away - on every stream change. Never
      //    for zoom+zoom (the meeting is the stream) nor Web Page (the stream is the link
      //    the admin typed, Zoom feeds nothing). Not confirmed (admin not in the meeting,
      //    Zoom UI not ready) = ask the admin to turn it on; Go Live re-checks it.
      if (isHostingZoom && !picksHostingVendor && !needsWebPageUrl) {
        if (await enableZoomSdkLivestreamAndConfirm()) {
          showMsg?.("Custom Live Streaming started for the new stream", "success");
        } else {
          showMsg?.(ZOOM_TURN_ON_YOURSELF_MSG, "error");
        }
      }

      // Same vendor (zoom+zoom, ...): show the same-vendor steps now, without waiting
      // for CME's push (see setStreamingVendorLocal).
      if (picksHostingVendor) setStreamingVendorLocal(sessionId, selectedVendor.vendor);
    } catch (e) {
      showMsg?.(
        "Failed to generate streaming link: " + (e?.message || "unknown error"),
        "error",
      );
    } finally {
      setBusy(false);
    }
  }

  // Stopping needs no gate of its own, but still takes the lock - it must
  // not run while a Start or Generate is mid-flight.
  function stopStream() {
    if (busy) return;
    setBusy(true);
    try {
      toggleStreamToUser(sessionId, false);
      // Zoom hosting another vendor: also turn off Zoom's custom live streaming
      // (zoom+zoom never turned it on).
      if (needsZoomSdkToggle) {
        const zoomFrame = document.querySelector(
          'iframe[title="Zoom meeting"]',
        );
        zoomFrame?.contentWindow?.postMessage(
          { kind: "toggle-zoom-stream", streamState: false },
          window.location.origin,
        );
      }
      showMsg?.("Stream stopped", "success");
    } catch (e) {
      showMsg?.("Couldn't stop the stream: " + (e?.message || "unknown error"), "error");
    } finally {
      setBusy(false);
    }
  }

  // Going live: every step below runs one at a time, in order - never in
  // parallel - and each one must pass before viewers are told "it's live".
  // The Go Live modal shows each check as it runs; the first one that fails stays on
  // screen (red cross + why) and nothing goes live.
  async function startStream() {
    if (busy) return;
    setBlockedBroadcastUrl(null); // a notice from an earlier attempt
    const run = { cancelled: false };
    goLiveRunRef.current = run;
    const timing = goLiveTiming();
    let total = goLiveCheckCount(deriveGoLiveFacts(session));
    let number = 0;
    let current = { id: "live", label: "Going live" }; // the check on screen (errors land on it)
    const show = (phase, step) => {
      if (!run.cancelled) setGoLiveCheck({ phase, number, total, step });
    };
    const fail = (failure) => show("failed", { ...current, status: "failed", ...failure });

    // Shows one check: its "checking" line (with a countdown when the check has a time
    // limit of its own), then the green tick for a moment. `work` runs the check and
    // answers a failure ({ text, detail, action? }) or nothing when it passed.
    // Resolves whether Go Live may carry on (false = failed or cancelled).
    async function check({ id, label, runningText, countdownMs }, work) {
      if (run.cancelled) return false;
      number += 1;
      current = { id, label };
      const startedAt = Date.now();
      show("running", {
        id,
        label,
        status: "running",
        text: runningText,
        startedAt,
        deadline: countdownMs ? startedAt + countdownMs : null,
      });
      const [failure] = await Promise.all([work(), sleep(timing.minRunMs)]);
      if (run.cancelled) return false;
      if (failure) {
        fail(failure);
        return false;
      }
      return true;
    }
    async function passed(text) {
      show("running", { ...current, status: "passed", text });
      await sleep(timing.passHoldMs);
      return !run.cancelled;
    }

    const noHostVendor = {
      text: "No meeting vendor",
      detail: "This session has no meeting (hosting) vendor yet, so the stream can't start. Set up the meeting first, then try again.",
    };
    const noStreamingVendor = {
      text: "No streaming vendor",
      detail: `No stream has been added to this session yet. Use ${streamActionLabel} to pick a streaming vendor, then try again.`,
      action: "stream",
    };
    // Go Live needs both the meeting (hosting) vendor and the streaming vendor in
    // cme-common. A missing meeting vendor is reported first (Change Stream can't fix
    // it - its list is per hosting vendor); a missing streaming vendor offers Add Stream.
    const missingVendor = (facts) =>
      facts.hasVendors ? null : facts.hostingVendor ? noStreamingVendor : noHostVendor;

    // Missing vendors on the rendered session: answer the click at once, no refresh.
    const missingNow = missingVendor(deriveGoLiveFacts(session));
    if (missingNow) {
      number = 1;
      current =
        missingNow === noHostVendor
          ? { id: "host", label: "Meeting vendor" }
          : { id: "vendor", label: "Streaming vendor" };
      fail(missingNow);
      return;
    }

    setBusy(true);
    try {
      // 1. force-fetch cme-common first - don't trust a possibly-stale cache for a
      //    decision this important (see refreshCmeSessionOnce's comment). Bounded:
      //    a dead/hung refresh falls back to the rendered session instead of leaving
      //    the button stuck on "Working...".
      let fresh = session;
      if (
        !(await check(
          { id: "fetch", label: "Session data", runningText: "Fetching the latest data…", countdownMs: FETCH_COUNTDOWN_MS },
          async () => {
            fresh = (await refreshWithFallback(sessionId, session)) || session;
          },
        ))
      )
        return;
      if (!(await passed("Latest data fetched"))) return;
      const facts = deriveGoLiveFacts(fresh);
      total = goLiveCheckCount(facts);

      // 2. meeting vendor + streaming vendor must both be in the fresh cme-common data
      const missing = missingVendor(facts);
      if (
        !(await check(
          { id: "host", label: "Meeting vendor", runningText: "Checking the meeting vendor…" },
          async () => (missing === noHostVendor ? noHostVendor : null),
        ))
      )
        return;
      if (!(await passed(`Meeting: ${facts.hostingVendor}`))) return;
      if (
        !(await check(
          { id: "vendor", label: "Streaming vendor", runningText: "Checking the streaming vendor…" },
          async () => missing,
        ))
      )
        return;
      if (!(await passed(`Streaming: ${facts.streamingVendor}`))) return;

      //    session must be live + a way to stream must exist
      //    (same vendor on both sides passes without a streaming_url)
      if (
        !(await check(
          { id: "status", label: "Session status", runningText: "Checking the session is live…" },
          async () =>
            facts.isSessionLive
              ? null
              : { text: "Session isn't live", detail: "The stream can only be started while the session is live." },
        ))
      )
        return;
      if (!(await passed("Session is live"))) return;
      if (!facts.isSameVendor) {
        if (
          !(await check(
            { id: "link", label: "Stream link", runningText: "Checking the stream link…" },
            async () =>
              facts.canGoLive
                ? null
                : {
                    text: "No streaming link yet",
                    detail: `This session doesn't have a streaming link yet, so the stream can't start. Use ${streamActionLabel} to generate one first.`,
                    action: "stream",
                  },
          ))
        )
          return;
        if (!(await passed("Link confirmed"))) return;
      }

      // 3. zoom hosting another vendor: confirm Zoom's custom live streaming is
      //    really on. zoom+zoom / other hosts: no Zoom SDK toggle to confirm.
      if (facts.needsZoomSdkToggle) {
        if (
          !(await check(
            {
              id: "zoom",
              label: "Zoom Custom Live Streaming",
              runningText: "Turning on Custom Live Streaming in Zoom…",
              countdownMs: ZOOM_COUNTDOWN_MS,
            },
            async () =>
              (await enableZoomSdkLivestreamAndConfirm())
                ? null
                : {
                    text: "Please enable Zoom Custom Live Streaming",
                    detail: ZOOM_TURN_ON_YOURSELF_MSG,
                  },
          ))
        )
          return;
        if (!(await passed("Custom Live Streaming is on"))) return;
      }

      // Web Page stream: the link the admin typed in is the stream - a custom src with no
      // Mux behind it (no health telemetry) that need not be live. One check only: can a
      // viewer's player open that link (cme-common's current streaming url)?
      if (facts.isWebPageStream && !facts.isSameVendor) {
        if (
          !(await check(
            {
              id: "playing",
              label: "Stream playing",
              runningText: "Checking the link plays…",
              countdownMs: LINK_CHECK_TIMEOUT_MS,
            },
            async () => {
              const result = await checkLinkPlayable(facts.streamingUrl);
              if (result.playable) return null;
              return {
                text: "Link isn't playable",
                detail: `${linkCheckReasonText(result.reason)}. Check the web page stream link with ${streamActionLabel}, then try again.`,
                action: "stream",
              };
            },
          ))
        )
          return;
        if (!(await passed("Link is playable"))) return;
      }

      // 4. URL-based stream: viewers get pointed at a URL, so the feed must already
      //    be Started (Mux telemetry on session.data.health). Deliberately
      //    AFTER the Zoom SDK step - that step is what starts the feed, so checking
      //    first could never pass on a first click. Same-vendor streams have no URL
      //    to warm up, so they skip this entirely, and so do Web Page streams (above).
      //    SKIP_STREAM_HEALTH_CHECK skips it: straight on to step 5.
      if (!facts.isSameVendor && !facts.isWebPageStream) {
        if (!SKIP_STREAM_HEALTH_CHECK) {
          if (
            !(await check(
              { id: "health", label: "Stream health", runningText: "Checking the stream health…" },
              async () => {
                const health = deriveStreamHealth(fresh?.data?.health);
                if (!health) {
                  return {
                    text: "No streaming health data",
                    detail: "We haven't received any health telemetry for this stream yet, so we can't confirm it's up. Wait for the stream to start, then try again.",
                  };
                }
                if (!health.started) {
                  return {
                    text: "Stream isn't ready yet",
                    detail: "The stream has telemetry but it isn't Started yet - Mux isn't receiving the feed. Wait until it is, then try again.",
                  };
                }
                return null;
              },
            ))
          )
            return;
          if (!(await passed("Mux is receiving the feed"))) return;
        }

        // 5. Playing: a URL that responds is not a stream a viewer can watch. Shared with
        //    the Stream Health card: its current "Playing: Yes" answers at once; otherwise
        //    the force check runs now (fresh manifest, segment bytes, timeline actually
        //    advancing, a few seconds) and the card shows its result too.
        //    Not playable yet (e.g. a just-started stream whose playlist isn't up): keep
        //    checking until it plays or the admin cancels - what the old "Stream not ready"
        //    modal did - then go live straight away (the other checks already passed).
        if (
          !(await check(
            { id: "playing", label: "Stream playing", runningText: "Checking the stream plays…" },
            async () => {
              let playback = await checkStreamPlaying(facts.streamingUrl);
              while (!playback.playing && !run.cancelled) {
                show("running", {
                  ...current,
                  status: "running",
                  text: `Stream not ready yet - last check: ${playbackReasonText(playback.reason)}. Checking again…`,
                });
                await sleep(timing.playbackRecheckMs);
                if (run.cancelled) break;
                playback = await checkStreamPlaying(facts.streamingUrl);
              }
              return null; // playing (or cancelled - check() stops on that)
            },
          ))
        )
          return;
        if (!(await passed("Stream is playing"))) return;
      }
      if (run.cancelled) return;
      current = { id: "live", label: "Going live" };

      // toggleStreamToUser silently does nothing when the admin store has no entry for
      // this session id - seed one from the session we just refreshed so the toggle
      // always lands instead of reading as a dead button.
      if (!getSessionById(sessionId)) setSessionDetails(sessionId, fresh);

      toggleStreamToUser(sessionId, true);
      if (getSessionById(sessionId)?.data?.streamToUser !== true) {
        fail({
          text: "Couldn't start the stream",
          detail: "The session isn't loaded yet. Please try again.",
        });
        return;
      }
      show("live", { id: "live", label: "You're live", status: "passed", text: "Viewers can now watch the stream." });
      showMsg?.("Stream is live", "success");
    } catch (e) {
      // Anything else that throws must surface - a silent failure here looks exactly
      // like a dead button.
      fail({ text: "Couldn't start the stream", detail: e?.message || "unknown error" });
    } finally {
      // A cancelled run already gave the button back (and a new run may own it now).
      if (goLiveRunRef.current === run && !run.cancelled) setBusy(false);
    }
  }

  // Cancel (while checking) / Close: the running Go Live stops - no further check runs
  // and it never goes live - and Go Live can be clicked again straight away.
  const closeGoLiveCheck = useCallback(() => {
    const run = goLiveRunRef.current;
    if (run && !run.cancelled) {
      run.cancelled = true;
      setBusy(false);
    }
    setGoLiveCheck(null);
    setBlockedBroadcastUrl(null);
  }, []);

  // The blocked pop-up notice's button: opened inside the admin's click, so the pop-up
  // blocker lets it through. Blocked anyway: the notice stays.
  function openBlockedBroadcastPage() {
    const tab = blockedBroadcastUrl ? window.open(blockedBroadcastUrl, "_blank") : null;
    if (!tab) return;
    broadcastTabRef.current = tab;
    setBlockedBroadcastUrl(null);
  }
  // Shown on the Zoom check only, until Zoom is live.
  const showPopupBlocked =
    Boolean(blockedBroadcastUrl) && !isZoomLive && goLiveCheck?.step?.id === "zoom";

  // "You're live" closes by itself after a moment.
  useEffect(() => {
    if (goLiveCheck?.phase !== "live") return undefined;
    const timer = setTimeout(() => setGoLiveCheck(null), goLiveTiming().liveHoldMs);
    return () => clearTimeout(timer);
  }, [goLiveCheck?.phase]);

  function handleToggleStream() {
    if (busy) return;
    if (session.data?.streamToUser) stopStream();
    else startStream();
  }

  // A failed vendor / link check jumps straight into Add / Change Stream.
  function fixStreamFromGoLiveCheck() {
    setGoLiveCheck(null);
    openPublishModal();
  }

  // Shared by the top-bar Go Live button and the floating one on phones.
  const liveButtonTone = session.data?.streamToUser
    ? "text-white bg-red-600 hover:bg-red-700"
    : "text-white bg-emerald-600 hover:bg-emerald-700";
  const liveButtonLabel = busy
    ? "Working..."
    : session.data?.streamToUser
      ? "Stop Live"
      : "Go Live";

  // Always rendered (no Zoom-join gate). Go Live still runs its own checks in
  // startStream, so showing the controls early can't put viewers live too soon.
  return (
    <div className="flex items-center gap-2">
      {/* Horizontal Status Area */}
      <div className="hidden md:flex items-center gap-3 mr-3">
        {/* Visual Step Indicator replacing the old plain text status */}
        <StepIndicator steps={steps} />
      </div>

      {/* Phones / small tablets: the same steps in their own row above Stream Health */}
      {mobileStepsSlot &&
        createPortal(
          <section
            className="bg-white rounded-2xl border border-gray-200/70 px-3 py-2 overflow-x-auto [scrollbar-width:none]"
            aria-label="Streaming steps"
          >
            <StepIndicator steps={steps} />
          </section>,
          mobileStepsSlot,
        )}

      {/* Change Stream - opens the Publish modal (pick vendor + generate link).
          A plain button (not the shared <Button>) so it can match Go Live exactly:
          same padding / text size / rounded-lg, and no drop shadow (the shared
          primary Button adds shadow-lg). Disabled while vendors load or any
          Generate / Start / Stop flow holds the `busy` lock. */}
      <button
        type="button"
        onClick={openPublishModal}
        disabled={isVendorsLoading || busy}
        className="inline-flex items-center justify-center px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium rounded-lg text-white bg-gradient-to-r from-gray-800 to-black hover:from-gray-900 hover:to-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {streamActionLabel}
      </button>

      {/* Start/Stop - see handleToggleStream / startStream / stopStream.
          Disabled while busy so a click can't land mid-check. type="button" so a
          stray form ancestor can never turn the click into a submit. */}
      <button
        type="button"
        onClick={handleToggleStream}
        disabled={busy}
        className={`hidden sm:inline-flex px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${liveButtonTone}`}
      >
        {liveButtonLabel}
      </button>

      {/* Phones (< sm): the same button floating full-width at the bottom of the screen.
          Portaled to <body> - the top bar is translated (hide on scroll), and a transformed
          ancestor would pin a fixed element to the bar instead of the viewport. */}
      {isPhone &&
        createPortal(
          <div className="sm:hidden fixed inset-x-0 bottom-0 z-40 px-3 pt-2 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-gradient-to-t from-gray-50 via-gray-50/90 to-transparent dark:from-[#0b0f17] dark:via-[#0b0f17]/90">
            <button
              type="button"
              onClick={handleToggleStream}
              disabled={busy}
              className={`w-full py-3 text-sm font-semibold rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${liveButtonTone}`}
            >
              {liveButtonLabel}
            </button>
          </div>,
          document.body,
        )}

      {/* No meeting vendor: locked notice, no way past it (missingMeetingVendor) */}
      {missingMeetingVendor &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="no-meeting-vendor-title"
              aria-describedby="no-meeting-vendor-message"
              className="bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-sm p-6 text-center"
            >
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
                <svg className="h-6 w-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v3.75m0 3.75h.008M10.34 3.94l-8.4 14.55A1.5 1.5 0 003.24 21h17.52a1.5 1.5 0 001.3-2.51L13.66 3.94a1.5 1.5 0 00-2.6 0z"
                  />
                </svg>
              </div>
              <h3 id="no-meeting-vendor-title" className="text-base font-semibold text-gray-800">
                No meeting vendor
              </h3>
              <p id="no-meeting-vendor-message" className="mt-1 text-sm text-gray-600">
                This session has no meeting vendor – please choose from admin.
              </p>
            </div>
          </div>,
          document.body,
        )}

      {/* Publish modal: pick a vendor, generate a link. With no streaming vendor yet it
          opens by itself and can't be closed (mustChooseStream) - no ×, no Cancel. */}
      {(showPublishModal || mustChooseStream) &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="publish-modal-title"
              aria-describedby={streamingVendorName ? "publish-modal-current" : undefined}
              className="bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-sm overflow-hidden"
            >
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
                <div className="min-w-0">
                  <h3 id="publish-modal-title" className="text-base font-semibold text-gray-800">
                    {streamActionLabel}
                  </h3>
                  {/* What is streaming now (cme-common streaming.vendor) - what a pick replaces. */}
                  {streamingVendorName && (
                    <p id="publish-modal-current" className="mt-0.5 truncate text-xs text-gray-500">
                      Current streaming vendor:{" "}
                      <span className="font-semibold text-gray-800">{streamingVendorName}</span>
                    </p>
                  )}
                </div>
                {!mustChooseStream && (
                  <button
                    onClick={closePublishModal}
                    aria-label="Close"
                    className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                  >
                    &times;
                  </button>
                )}
              </div>

              <div className="p-6 space-y-4">
                {mustChooseStream && (
                  <p className="text-sm text-gray-600">
                    This session doesn&apos;t have a streaming vendor yet. Choose one to continue.
                  </p>
                )}
                <VendorSelect
                  value={vendorId}
                  onChange={onVendorChange}
                  vendors={selectableVendors}
                  isLoading={isVendorsLoading}
                  isError={isVendorsError}
                />
                {/* A failed list would otherwise leave the locked modal with nothing to pick. */}
                {isVendorsError && (
                  <p className="text-xs text-red-600">
                    Couldn&apos;t load the streaming vendors.{" "}
                    <button
                      type="button"
                      onClick={() => refetchVendors()}
                      className="font-medium underline underline-offset-2 hover:text-red-700"
                    >
                      Retry
                    </button>
                  </p>
                )}

                {needsWebPageUrl && (
                  <div className="space-y-1.5">
                    <label htmlFor="web-page-url" className="block text-xs font-medium text-gray-700">
                      Web page stream link
                    </label>
                    <TextInput
                      id="web-page-url"
                      type="url"
                      inputMode="url"
                      autoComplete="off"
                      spellCheck={false}
                      value={webPageUrl}
                      onChange={(e) => setWebPageUrl(e.target.value)}
                      placeholder="https://…"
                      aria-invalid={webPageUrlError ? true : undefined}
                      aria-describedby={webPageUrlError ? "web-page-url-error" : undefined}
                      className={`!py-2 text-xs sm:text-sm${webPageUrlError ? " !border-red-500" : ""}`}
                    />
                    {webPageUrlError && (
                      <span id="web-page-url-error" className="block text-xs text-red-600">
                        {webPageUrlError}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-end gap-2">
                  {!mustChooseStream && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={closePublishModal}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleGenerateStreamingLink}
                    // disabled={
                    //   !selectedVendor ||
                    //   busy ||
                    //   isVendorsLoading ||
                    //   disableGenerate
                    // }
                  >
                    {busy ? "Generating..." : "Generate Streaming"}
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Go Live: every check, one at a time; goes live by itself once all pass */}
      {goLiveCheck && (
        <GoLiveCheckModal
          check={goLiveCheck}
          onClose={closeGoLiveCheck}
          onRetry={() => startStream()}
          onAction={fixStreamFromGoLiveCheck}
          actionLabel={streamActionLabel}
          popupBlocked={showPopupBlocked}
          onOpenBlockedPage={openBlockedBroadcastPage}
        />
      )}
    </div>
  );
}

export default StreamingVendorControls;
